import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getMovieById } from '@/mocks/movies';
import { getTVShowById } from '@/mocks/tvshows';
import { getPosterUrl } from '@/mocks/imageUrls';

export default function DetailPage() {
  const { type, id } = useParams<{ type: string; id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const item = type === 'movie' ? getMovieById(id || '') : getTVShowById(id || '');

  if (!item) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-800">
              <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-800 mb-2">内容未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该影视内容可能已下架或链接无效</p>
            <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-950 rounded-full font-medium text-sm hover:bg-primary-400 transition whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isMovie = type === 'movie';
  const categoryLabel = isMovie ? '电影' : '电视剧';
  const categoryPath = isMovie ? '/category/movie' : '/category/tv';

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 min-h-screen">
        <div className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabel, href: categoryPath },
                { label: item.title },
              ]}
            />

            <article className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0 lg:w-[340px]">
                <div className="rounded-xl overflow-hidden bg-background-800/50">
                  <img
                    src={getPosterUrl(item.posterSeq)}
                    alt={item.title}
                    title={`${item.title} - 奇优影院高清在线观看`}
                    className="w-full object-cover object-top"
                    style={{ maxHeight: '480px' }}
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(item as { tags?: string[] }).tags?.map((tag: string) => (
                    <span key={tag} className="px-3 py-1 bg-primary-500/10 text-primary-300 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  {item.title}
                </h1>
                <p className="text-foreground-500 text-sm mb-6">{item.titleEn}</p>

                <div className="flex items-center gap-3 mb-6">
                  <span className="px-3 py-1 bg-accent-500/15 text-accent-400 text-sm font-bold rounded-lg">
                    {item.rating}
                  </span>
                  <div className="flex items-center gap-1 text-accent-400 text-sm">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`text-sm ${star <= Math.round(item.rating / 2) ? 'ri-star-fill' : 'ri-star-line'}`}
                      ></i>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 p-6 rounded-xl bg-background-800/50 border border-background-700/30">
                  {[
                    { label: '类型', value: item.genre },
                    { label: '年份', value: String(item.year) },
                    { label: '导演', value: item.director },
                    { label: '时长', value: 'duration' in item ? item.duration : ('episodes' in item ? item.episodes : '-') },
                    { label: '评分', value: String(item.rating) },
                    { label: '更新日期', value: item.updateDate },
                  ].map((info) => (
                    <div key={info.label}>
                      <span className="text-foreground-500 text-xs block mb-1">{info.label}</span>
                      <span className="text-foreground-800 text-sm font-medium">{info.value}</span>
                    </div>
                  ))}
                </div>

                <div className="mb-8">
                  <h2 className="text-foreground-800 font-bold text-lg mb-3">主演</h2>
                  <div className="flex flex-wrap gap-2">
                    {item.cast.map((actor: string) => (
                      <span key={actor} className="px-4 py-2 bg-background-800 text-foreground-600 text-sm rounded-full whitespace-nowrap">
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-foreground-800 font-bold text-lg mb-3">剧情简介</h2>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {item.longDescription}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex-1 px-6 py-3 bg-primary-500 text-background-950 font-bold text-sm rounded-full hover:bg-primary-400 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill mr-2 text-base"></i>
                    立即播放
                  </button>
                  <button className="flex-1 px-6 py-3 bg-background-800 text-foreground-700 font-medium text-sm rounded-full hover:bg-background-700 border border-background-700 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-2 text-base"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </article>

            <section className="mt-16">
              <h2 className="text-xl font-heading font-bold text-foreground-800 mb-6">相关推荐</h2>
              <p className="text-foreground-500 text-sm">更多精彩内容敬请期待...</p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}